import React, { useEffect, useState, useMemo } from 'react';
import { emailApiService, EmailThread } from '../../services/email/emailApiService';
import { emailPollingService } from '../../services/email/emailPollingService';
import { EmailThreadView } from './EmailThreadView';
import {
    Mail, Search, Inbox, Send, RefreshCw, ChevronRight,
    MessageSquare, Clock, ArrowDownLeft, ArrowUpRight, X
} from 'lucide-react';

interface TenderInboxPanelProps {
    tenderId: string;
}

type TabType = 'threads' | 'sent';

const formatRelativeTime = (dateStr: string): string => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString('en-AU', { day: 'numeric', month: 'short' });
};

const getInitials = (name: string): string => {
    if (!name) return '?';
    return name.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2);
};

const avatarColors = [
    'bg-blue-500', 'bg-purple-500', 'bg-green-500', 'bg-orange-500',
    'bg-pink-500', 'bg-teal-500', 'bg-indigo-500', 'bg-rose-500'
];

const getAvatarColor = (name: string): string => {
    const idx = (name?.charCodeAt(0) || 0) % avatarColors.length;
    return avatarColors[idx];
};

export const TenderInboxPanel: React.FC<TenderInboxPanelProps> = ({ tenderId }) => {
    const [threads, setThreads] = useState<EmailThread[]>([]);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [activeTab, setActiveTab] = useState<TabType>('threads');
    const [search, setSearch] = useState('');
    const [selectedThreadId, setSelectedThreadId] = useState<string | null>(null);

    const fetchThreads = async (silent = false) => {
        try {
            if (!silent) setLoading(true);
            else setRefreshing(true);
            const data = await emailApiService.getThreads(tenderId);
            setThreads(data);
        } catch (err) {
            console.error('TenderInboxPanel: failed to load threads', err);
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    };

    useEffect(() => {
        fetchThreads();
        const unsub = emailPollingService.subscribe(() => fetchThreads(true));
        return () => unsub();
    }, [tenderId]);

    const filteredThreads = useMemo(() => {
        let list = threads;

        // Tab filter
        if (activeTab === 'sent') {
            // Threads that started outbound (we sent first)
            list = list.filter(t => t.status !== 'reply_received');
        }

        // Search
        if (search.trim()) {
            const q = search.toLowerCase();
            list = list.filter(t =>
                t.subject?.toLowerCase().includes(q) ||
                t.retailer?.name?.toLowerCase().includes(q) ||
                t.retailer?.contact_email?.toLowerCase().includes(q)
            );
        }

        return list;
    }, [threads, activeTab, search]);

    const inboxCount = threads.length;
    const hasReply = (t: EmailThread) =>
        t.status === 'reply_received' || (t.messages && t.messages[0]?.count > 1);

    const selectedThread = threads.find(t => t.id === selectedThreadId);

    return (
        <div className="flex flex-col h-full min-h-[500px] bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-3 border-b bg-gradient-to-r from-blue-50 to-indigo-50">
                <div className="flex items-center gap-2">
                    <div className="p-1.5 bg-blue-600 rounded-lg">
                        <Mail className="w-4 h-4 text-white" />
                    </div>
                    <div>
                        <h3 className="font-semibold text-gray-900 text-sm">Tender Inbox</h3>
                        <p className="text-xs text-gray-500">{inboxCount} thread{inboxCount !== 1 ? 's' : ''}</p>
                    </div>
                </div>
                <button
                    onClick={() => fetchThreads(true)}
                    disabled={refreshing}
                    className="p-1.5 rounded-md text-gray-500 hover:bg-white hover:text-blue-600 transition-colors"
                    title="Refresh"
                >
                    <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
                </button>
            </div>

            <div className="flex flex-1 overflow-hidden">
                {/* Left: Thread List */}
                <div className={`flex flex-col border-r ${selectedThreadId ? 'w-[340px] min-w-[260px]' : 'flex-1'} transition-all`}>
                    {/* Tabs + Search */}
                    <div className="p-3 space-y-2 border-b bg-gray-50">
                        {/* Tabs */}
                        <div className="flex gap-1 bg-gray-200 rounded-lg p-0.5">
                            <button
                                onClick={() => setActiveTab('threads')}
                                className={`flex-1 flex items-center justify-center gap-1.5 py-1.5 rounded-md text-xs font-medium transition-all ${
                                    activeTab === 'threads'
                                        ? 'bg-white text-blue-600 shadow-sm'
                                        : 'text-gray-500 hover:text-gray-700'
                                }`}
                            >
                                <Inbox className="w-3.5 h-3.5" />
                                All Threads
                                {inboxCount > 0 && (
                                    <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-semibold ${
                                        activeTab === 'threads' ? 'bg-blue-100 text-blue-700' : 'bg-gray-300 text-gray-600'
                                    }`}>
                                        {inboxCount}
                                    </span>
                                )}
                            </button>
                            <button
                                onClick={() => setActiveTab('sent')}
                                className={`flex-1 flex items-center justify-center gap-1.5 py-1.5 rounded-md text-xs font-medium transition-all ${
                                    activeTab === 'sent'
                                        ? 'bg-white text-blue-600 shadow-sm'
                                        : 'text-gray-500 hover:text-gray-700'
                                }`}
                            >
                                <Send className="w-3.5 h-3.5" />
                                Sent
                            </button>
                        </div>

                        {/* Search */}
                        <div className="relative">
                            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
                            <input
                                type="text"
                                value={search}
                                onChange={e => setSearch(e.target.value)}
                                placeholder="Search retailer, subject..."
                                className="w-full pl-8 pr-3 py-1.5 text-xs bg-white border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-400"
                            />
                            {search && (
                                <button
                                    onClick={() => setSearch('')}
                                    className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                                >
                                    <X className="w-3 h-3" />
                                </button>
                            )}
                        </div>
                    </div>

                    {/* Thread List */}
                    <div className="flex-1 overflow-y-auto">
                        {loading ? (
                            <div className="space-y-0">
                                {Array.from({ length: 5 }).map((_, i) => (
                                    <div key={i} className="p-3 border-b flex gap-3 animate-pulse">
                                        <div className="w-8 h-8 rounded-full bg-gray-200 flex-shrink-0" />
                                        <div className="flex-1 space-y-2">
                                            <div className="h-3 bg-gray-200 rounded w-3/4" />
                                            <div className="h-2.5 bg-gray-100 rounded w-full" />
                                            <div className="h-2 bg-gray-100 rounded w-1/2" />
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : filteredThreads.length === 0 ? (
                            <div className="flex flex-col items-center justify-center h-full py-12 px-4 text-center">
                                <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center mb-3">
                                    <Mail className="w-6 h-6 text-gray-300" />
                                </div>
                                <p className="text-sm font-medium text-gray-500">
                                    {search ? 'No results found' : 'No emails yet'}
                                </p>
                                <p className="text-xs text-gray-400 mt-1">
                                    {search ? 'Try a different search term' : 'Emails sent to retailers will appear here'}
                                </p>
                            </div>
                        ) : (
                            filteredThreads.map(thread => {
                                const isSelected = selectedThreadId === thread.id;
                                const retailerName = thread.retailer?.name || 'Unknown Retailer';
                                const msgCount = thread.messages?.[0]?.count ?? 1;
                                const replied = hasReply(thread);

                                return (
                                    <div
                                        key={thread.id}
                                        onClick={() => setSelectedThreadId(isSelected ? null : thread.id)}
                                        className={`group flex items-start gap-3 px-3 py-3 cursor-pointer border-b border-gray-100 transition-colors ${
                                            isSelected
                                                ? 'bg-blue-50 border-l-2 border-l-blue-500'
                                                : 'hover:bg-gray-50 border-l-2 border-l-transparent'
                                        }`}
                                    >
                                        {/* Avatar */}
                                        <div className={`w-8 h-8 rounded-full flex-shrink-0 flex items-center justify-center text-white text-xs font-bold ${getAvatarColor(retailerName)}`}>
                                            {getInitials(retailerName)}
                                        </div>

                                        {/* Content */}
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center justify-between gap-1 mb-0.5">
                                                <span className={`text-xs font-semibold truncate ${isSelected ? 'text-blue-700' : 'text-gray-800'}`}>
                                                    {retailerName}
                                                </span>
                                                <span className="text-[10px] text-gray-400 whitespace-nowrap flex items-center gap-1 flex-shrink-0">
                                                    <Clock className="w-2.5 h-2.5" />
                                                    {formatRelativeTime(thread.last_message_at)}
                                                </span>
                                            </div>
                                            <p className="text-xs text-gray-600 truncate mb-1">
                                                {thread.subject || '(No subject)'}
                                            </p>
                                            <div className="flex items-center gap-1.5">
                                                {replied ? (
                                                    <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full bg-green-100 text-green-700 font-medium">
                                                        <ArrowDownLeft className="w-2.5 h-2.5" />
                                                        Replied
                                                    </span>
                                                ) : (
                                                    <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full bg-blue-100 text-blue-700 font-medium">
                                                        <ArrowUpRight className="w-2.5 h-2.5" />
                                                        Sent
                                                    </span>
                                                )}
                                                {msgCount > 1 && (
                                                    <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-500">
                                                        <MessageSquare className="w-2.5 h-2.5" />
                                                        {msgCount}
                                                    </span>
                                                )}
                                            </div>
                                        </div>

                                        <ChevronRight className={`w-3.5 h-3.5 flex-shrink-0 mt-1 transition-colors ${isSelected ? 'text-blue-400' : 'text-gray-300 group-hover:text-gray-400'}`} />
                                    </div>
                                );
                            })
                        )}
                    </div>

                    {/* Footer count */}
                    {!loading && filteredThreads.length > 0 && (
                        <div className="px-3 py-2 border-t bg-gray-50 text-[10px] text-gray-400 text-center">
                            {filteredThreads.length} thread{filteredThreads.length !== 1 ? 's' : ''}
                            {search && ` matching "${search}"`}
                        </div>
                    )}
                </div>

                {/* Right: Thread View */}
                {selectedThreadId && (
                    <div className="flex-1 flex flex-col overflow-hidden bg-white">
                        {/* Thread header */}
                        <div className="flex items-center justify-between px-4 py-2.5 border-b bg-gray-50">
                            <div className="flex items-center gap-2 min-w-0">
                                <div className={`w-6 h-6 rounded-full flex-shrink-0 flex items-center justify-center text-white text-[10px] font-bold ${getAvatarColor(selectedThread?.retailer?.name || '')}`}>
                                    {getInitials(selectedThread?.retailer?.name || '')}
                                </div>
                                <div className="min-w-0">
                                    <p className="text-xs font-semibold text-gray-800 truncate">
                                        {selectedThread?.retailer?.name || 'Unknown'}
                                    </p>
                                    <p className="text-[10px] text-gray-500 truncate">
                                        {selectedThread?.subject || '(No subject)'}
                                    </p>
                                </div>
                            </div>
                            <button
                                onClick={() => setSelectedThreadId(null)}
                                className="p-1 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-200 transition-colors flex-shrink-0"
                            >
                                <X className="w-4 h-4" />
                            </button>
                        </div>
                        <div className="flex-1 overflow-hidden">
                            <EmailThreadView threadId={selectedThreadId} />
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};
